import { ApiResponse } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'

class ApiService {
  private async request<T>(
    endpoint: string,
    options: any = {}
  ): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        ...options,
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.message || `HTTP error! status: ${response.status}`,
        }
      }

      return {
        success: true,
        data,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
      }
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  // Auth endpoints
  async login(email: string, password: string) {
    return this.post<{ token: string; user: any }>('/auth/login', { email, password })
  }

  async register(userData: {
    email: string
    password: string
    name: string
  }) {
    return this.post<{ token: string; user: any }>('/auth/register', userData)
  }

  async forgotPassword(email: string) {
    return this.post('/auth/forgot-password', { email })
  }

  async resetPassword(token: string, password: string) {
    return this.post('/auth/reset-password', { token, password })
  }

  // Health metrics endpoints
  async getHealthMetrics() {
    return this.get('/health-metrics')
  }

  async createHealthMetric(metric: any) {
    return this.post('/health-metrics', metric)
  }

  // Recommendations endpoints
  async getRecommendations() {
    return this.get('/recommendations')
  }

  async updateRecommendation(id: string, updates: any) {
    return this.put(`/recommendations/${id}`, updates)
  }

  // User endpoints
  async getUserProfile() {
    return this.get('/users/me')
  }

  async updateUserProfile(updates: any) {
    return this.put('/users/me', updates)
  }

  // Device endpoints
  async getDevices() {
    return this.get('/devices')
  }

  async connectDevice(deviceData: any) {
    return this.post('/devices', deviceData)
  }

  async syncDevice(deviceId: string) {
    return this.post(`/devices/${deviceId}/sync`)
  }

  // Test results endpoints
  async getTestResults() {
    return this.get('/test-results')
  }

  async uploadTestResults(testData: any) {
    return this.post('/test-results', testData)
  }

  // Meal plans endpoints
  async getMealPlans() {
    return this.get('/meal-plans')
  }

  async createMealPlan(mealPlan: any) {
    return this.post('/meal-plans', mealPlan)
  }

  // Supplements endpoints
  async getSupplements() {
    return this.get('/supplements')
  }

  async getSupplementRecommendations() {
    return this.get('/supplements/recommendations')
  }
}

export const apiService = new ApiService()